import { useParams } from 'react-router-dom';
import { DefaultError } from '@tanstack/query-core';
import { UseSuspenseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { fetchTransactionById } from 'api/transactions';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { flow, identity } from 'lodash';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { Transaction } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface TransformedTransaction extends ReturnType<typeof transformTransaction> {}

interface UseCurrentTransactionOrByIdOptions<T extends Transaction>
    extends Omit<UseSuspenseQueryOptions<Transaction, DefaultError, T, any>, 'queryKey'> {
    transactionId?: number;
}

type UseCurrentPossibleTransactionOptions = Omit<
    UseSuspenseQueryOptions<Transaction | null, GivebutterAxiosError, Transaction>,
    'queryKey' | 'queryFn'
>;

interface TransactionSource {
    key: string;
    value: string;
}

const useCurrentTransactionOrById = <T extends Transaction>(
    options: UseCurrentTransactionOrByIdOptions<T> = {},
) => {
    const { transactionId: id, select, ...restOptions } = options;
    const { accountId } = useCurrentAccount();
    const params = useParams<{ transaction_id: string }>();
    const transactionId = !!id ? id : parseInt(params.transaction_id);

    return useSuspenseQuery({
        queryKey: transactionQueryKeys.transaction(accountId, transactionId),
        queryFn: () => fetchTransactionById(transactionId),
        select: flow(select ?? identity, transformTransaction) as (
            data: Transaction,
        ) => T & TransformedTransaction,
        ...restOptions,
    });
};

const useCurrentPossibleTransaction = <T extends Transaction>({
    retry = false,
    select,
    ...restOptions
}: UseCurrentPossibleTransactionOptions = {}) => {
    const { accountId } = useCurrentAccount();
    const { transaction_id } = useParams<{ transaction_id: string }>();
    const transactionId = transaction_id ? parseInt(transaction_id) : undefined;

    return useSuspenseQuery({
        queryKey: transactionQueryKeys.transaction(accountId, transactionId!),
        queryFn: () =>
            conditionallyFetchOrNull(() => fetchTransactionById(transactionId!), !!transactionId),
        select: transaction => {
            if (!transaction) return null;

            return (
                flow(select ?? identity, transformTransaction) as (
                    data: Transaction,
                ) => T & TransformedTransaction
            )(transaction);
        },
        retry,
        ...restOptions,
    });
};

const transformTransaction = (transaction: Transaction) => {
    return {
        ...transaction,
        tickets: transaction.tickets ?? [],
        sources: flow(
            transformUtm(transaction.utm),
            transformWidgetSource(transaction.widget_id),
        )([]),
    };
};

const transformUtm = (utm: Transaction['utm']) => (existingSources: TransactionSource[]) => {
    return [
        ...existingSources,
        ...Object.entries(utm ?? {}).map(([key, value]) => ({
            key,
            value,
        })),
    ];
};

const transformWidgetSource =
    (widget_id: Transaction['widget_id']) => (existingSources: TransactionSource[]) => {
        if (widget_id) {
            return [...existingSources, { key: 'Widget ID', value: widget_id }];
        }
        return existingSources;
    };

export { useCurrentTransactionOrById, useCurrentPossibleTransaction };
